import React, { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { TierBadge } from "@/components/Badges";
import { Button } from "@/components/ui/button";
import { LayoutDashboard, Gift, Package, Bell, Sparkles, LogOut, Menu, X, Clock, XCircle, RefreshCw, Loader2, Ban, Lock, CreditCard, LifeBuoy, ReceiptText } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";

const nav = [
  { to: "/portal", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/portal/catalogo", label: "Catálogo", icon: Gift, testid: "nav-catalog", feature: "catalog" },
  { to: "/portal/beneficios", label: "Beneficios", icon: Sparkles, testid: "nav-benefits", feature: "benefits" },
  { to: "/portal/planes", label: "Mis planes", icon: CreditCard, testid: "nav-plans" },
  { to: "/portal/facturas", label: "Mis facturas", icon: ReceiptText, testid: "nav-invoices", feature: "invoices" },
  { to: "/portal/notificaciones", label: "Notificaciones", icon: Bell, testid: "nav-notifications", feature: "notifications" },
  { to: "/portal/soporte", label: "Soporte", icon: LifeBuoy, testid: "nav-support" },
];

export default function CustomerLayout() {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [checking, setChecking] = useState(false);
  const [features, setFeatures] = useState<any>(null);

  useEffect(() => {
    api.get("/features/me").then((r) => setFeatures(r.data)).catch(() => {});
  }, [location.pathname]);

  useEffect(() => {
    api.get("/customer/dashboard").then((r) => setUnread(r.data.unread_notifications)).catch(() => {});
  }, [location.pathname]);

  const approval = user?.approval_status || "approved";
  const blocked = user?.status === "inactive";

  useEffect(() => {
    if (approval === "approved" && !blocked) return;
    const id = setInterval(() => { refresh(); }, 12000);
    return () => clearInterval(id);
  }, [approval, blocked]); // eslint-disable-line

  const doLogout = () => {
    logout();
    navigate("/");
  };

  const checkStatus = async () => {
    setChecking(true);
    try {
      await refresh();
    } finally {
      setTimeout(() => setChecking(false), 500);
    }
  };

  if (blocked) {
    return (
      <div className="min-h-screen bg-obsidian text-slate-900 flex items-center justify-center px-6" data-testid="account-blocked-screen">
        <div className="max-w-md w-full card-obsidian p-8 text-center">
          <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-6 bg-red-500/15">
            <Ban className="w-8 h-8 text-red-700" />
          </div>
          <div className="flex items-center justify-center mb-4">
            <BrandLogo />
          </div>
          <h1 className="text-2xl font-serif font-medium mb-2">Cuenta inhabilitada</h1>
          <p className="text-sm text-slate-500 leading-relaxed">
            Hola {user?.name}, tu cuenta está inhabilitada temporalmente y no tienes acceso a la plataforma.
            {user?.inactive_reason
              ? <> Motivo: <span className="text-slate-800">{user.inactive_reason}</span></>
              : " Contacta a Armony para reactivar tu membresía."}
          </p>
          {user?.inactive_reason && (
            <p className="text-xs text-slate-500 mt-3">Si ya regularizaste tu situación, consulta tu estado o contacta a Armony.</p>
          )}
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <Button data-testid="blocked-check-status-button" onClick={checkStatus} disabled={checking} className="gold-gradient-bg text-white font-semibold rounded-full">
              {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RefreshCw className="w-4 h-4 mr-2" /> Consultar estado</>}
            </Button>
            <Button data-testid="blocked-logout-button" onClick={doLogout} variant="outline" className="border-slate-200 text-slate-700 hover:bg-slate-100 rounded-full">
              <LogOut className="w-4 h-4 mr-2" /> Cerrar sesión
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (approval !== "approved") {
    const rejected = approval === "rejected";
    return (
      <div className="min-h-screen bg-obsidian text-slate-900 flex items-center justify-center px-6" data-testid="approval-pending-screen">
        <div className="max-w-md w-full card-obsidian p-8 text-center">
          <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-6 ${rejected ? "bg-red-500/15" : "bg-amber-500/15"}`}>
            {rejected ? <XCircle className="w-8 h-8 text-red-700" /> : <Clock className="w-8 h-8 text-amber-700" />}
          </div>
          <div className="flex items-center justify-center mb-4">
            <BrandLogo />
          </div>
          <h1 className="text-2xl font-serif font-medium mb-2">
            {rejected ? "Registro no aprobado" : "Cuenta en revisión"}
          </h1>
          <p className="text-sm text-slate-500 leading-relaxed">
            {rejected
              ? (user?.approval_reason
                  ? `Tu registro no fue aprobado. Motivo: ${user.approval_reason.replace(/[.\s]+$/, "")}. Contacta a Armony para más información sobre tu enrolamiento.`
                  : "Lamentablemente tu registro no fue aprobado. Contacta a Armony para más información sobre tu enrolamiento.")
              : `Hola ${user?.name}, tu registro fue recibido y está en proceso de aceptación por un administrador. Esta pantalla se actualizará automáticamente al aprobarse tu cuenta.`}
          </p>
          {!rejected && (
            <div className="mt-5 flex items-center justify-center gap-2 text-xs text-amber-700/80">
              <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
              Comprobando estado en tiempo real…
            </div>
          )}
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            {!rejected && (
              <Button data-testid="check-status-button" onClick={checkStatus} disabled={checking} className="gold-gradient-bg text-white font-semibold rounded-full">
                {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RefreshCw className="w-4 h-4 mr-2" /> Consultar estado</>}
              </Button>
            )}
            <Button data-testid="pending-logout-button" onClick={doLogout} variant="outline" className="border-slate-200 text-slate-700 hover:bg-slate-100 rounded-full">
              <LogOut className="w-4 h-4 mr-2" /> Cerrar sesión
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen overflow-hidden bg-obsidian text-slate-900 flex">
      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-navy border-r border-white/10 flex flex-col transition-transform ${
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="h-16 flex items-center px-6 border-b border-white/10">
          <BrandLogo bare tag="Contigo" />
        </div>
        <div className="p-4 border-b border-white/10">
          <p className="text-sm text-white font-medium truncate">{user?.name}</p>
          <div className="mt-2 flex items-center justify-between">
            <TierBadge tier={user?.tier || "Bronce"} />
            <span data-testid="sidebar-points" className="text-brand-salmon font-mono text-sm font-semibold">
              {user?.points?.toLocaleString()} pts
            </span>
          </div>
        </div>
        <nav className="flex-1 min-h-0 overflow-y-auto p-3 space-y-1">
          {nav.filter((n) => !n.feature || !features || features[n.feature]?.enabled !== false).map((n) => {
            const locked = !!(n.feature && features && features[n.feature] && !features[n.feature].available);
            return (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  isActive ? "bg-gold text-navy-dark font-semibold" : "text-white/70 hover:text-white hover:bg-white/10"
                }`
              }
            >
              <n.icon className="w-4.5 h-4.5" style={{ width: 18, height: 18 }} />
              <span className="flex-1">{n.label}</span>
              {locked && <Lock className="w-3.5 h-3.5 text-white/50" />}
              {n.to === "/portal/notificaciones" && unread > 0 && (
                <span className="w-5 h-5 rounded-full bg-gold text-navy-dark text-xs font-bold flex items-center justify-center">
                  {unread}
                </span>
              )}
            </NavLink>
          );})}
        </nav>
        <div className="p-3 border-t border-white/10">
          <Button
            data-testid="logout-button"
            variant="ghost"
            onClick={doLogout}
            className="w-full justify-start text-white/70 hover:text-white hover:bg-white/10"
          >
            <LogOut className="w-4 h-4 mr-2" /> Cerrar sesión
          </Button>
        </div>
      </aside>

      {open && <div className="fixed inset-0 bg-black/60 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      <div className="flex-1 min-w-0 flex flex-col min-h-0">
        <header className="h-16 lg:hidden flex items-center justify-between px-4 border-b border-white/10 bg-navy shrink-0">
          <button onClick={() => setOpen(!open)} className="text-white" data-testid="mobile-menu-toggle">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
          <Link to="/portal">
            <BrandLogo bare />
          </Link>
          <span className="text-brand-salmon font-mono text-sm">{user?.points?.toLocaleString()}</span>
        </header>
        <main className="flex-1 overflow-y-auto p-5 sm:p-8">
          <div className="max-w-6xl mx-auto w-full">
            <Outlet context={{ features }} />
          </div>
        </main>
      </div>
    </div>
  );
}
